import React, { useState } from 'react';
import { User, Car, Briefcase, FileText, ChevronRight, ChevronLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { vehicleMakes, statesList, employmentTypes, housingTypes, vehicleYears } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreditApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    ssn: '',
    // Address
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    housingType: '',
    monthlyPayment: '',
    yearsAtAddress: '',
    // Employment
    employmentType: '',
    employerName: '',
    jobTitle: '',
    yearsEmployed: '',
    monthlyIncome: '',
    // Vehicle
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vinNumber: '',
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Employment', icon: Briefcase },
    { number: 3, title: 'Vehicle Info', icon: Car },
    { number: 4, title: 'Review', icon: FileText },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const formatSSN = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.ssn.trim()) newErrors.ssn = 'SSN is required';
      if (!formData.streetAddress.trim()) newErrors.streetAddress = 'Address is required';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
      if (!formData.housingType) newErrors.housingType = 'Housing type is required';
    }

    if (step === 2) {
      if (!formData.employmentType) newErrors.employmentType = 'Employment type is required';
      if (formData.employmentType && !['Retired', 'Unemployed', 'Student'].includes(formData.employmentType)) {
        if (!formData.employerName.trim()) newErrors.employerName = 'Employer name is required';
        if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
      }
      if (!formData.monthlyIncome.trim()) newErrors.monthlyIncome = 'Monthly income is required';
    }

    if (step === 3) {
      if (!formData.vehicleYear) newErrors.vehicleYear = 'Vehicle year is required';
      if (!formData.vehicleMake) newErrors.vehicleMake = 'Vehicle make is required';
      if (!formData.vehicleModel.trim()) newErrors.vehicleModel = 'Vehicle model is required';
      // VIN is optional but if provided, validate format
      if (formData.vinNumber && formData.vinNumber.length !== 17) {
        newErrors.vinNumber = 'VIN must be 17 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/applications`, formData);
      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="credit-form" className="bg-zinc-900 py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-12">
            <div className="w-20 h-20 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Application Submitted!</h2>
            <p className="text-gray-400 text-lg mb-8">
              Thank you for your application. Our team will review your information and contact you within 24-48 hours.
            </p>
            <div className="bg-zinc-900/50 rounded-xl p-6 text-left">
              <h3 className="text-teal-400 font-semibold mb-3">What's Next?</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span>Our finance team will review your application</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span>We'll contact you with financing options</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span>Schedule a visit to complete your purchase</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="credit-form" className="bg-zinc-900 py-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Credit Application</h2>
          <p className="text-gray-400 text-lg">Complete the form below to get pre-approved for financing</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-12 max-w-2xl mx-auto">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    currentStep >= step.number
                      ? 'bg-teal-500 text-zinc-900'
                      : 'bg-zinc-800 text-gray-500'
                  }`}
                >
                  <step.icon className="w-5 h-5" />
                </div>
                <span className={`text-sm mt-2 hidden sm:block ${
                  currentStep >= step.number ? 'text-teal-400' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                  currentStep > step.number ? 'bg-teal-500' : 'bg-zinc-800'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Container */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6 sm:p-8">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <User className="w-5 h-5 text-teal-400" />
                Personal Information
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-gray-300">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="John"
                  />
                  {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-gray-300">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Doe"
                  />
                  {errors.lastName && <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john@example.com"
                  />
                  {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                  />
                  {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label htmlFor="dateOfBirth" className="text-gray-300">Date of Birth *</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                  />
                  {errors.dateOfBirth && <p className="text-red-400 text-sm mt-1">{errors.dateOfBirth}</p>}
                </div>
                <div>
                  <Label htmlFor="ssn" className="text-gray-300">Social Security Number *</Label>
                  <Input
                    id="ssn"
                    value={formData.ssn}
                    onChange={(e) => handleInputChange('ssn', formatSSN(e.target.value))}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.ssn ? 'border-red-500' : ''}`}
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                  />
                  {errors.ssn && <p className="text-red-400 text-sm mt-1">{errors.ssn}</p>}
                </div>
              </div>

              <div className="border-t border-zinc-700 pt-6 mt-6">
                <h4 className="text-lg font-medium text-white mb-4">Current Address</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="sm:col-span-2">
                    <Label htmlFor="streetAddress" className="text-gray-300">Street Address *</Label>
                    <Input
                      id="streetAddress"
                      value={formData.streetAddress}
                      onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                      className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.streetAddress ? 'border-red-500' : ''}`}
                      placeholder="123 Main Street"
                    />
                    {errors.streetAddress && <p className="text-red-400 text-sm mt-1">{errors.streetAddress}</p>}
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-gray-300">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.city ? 'border-red-500' : ''}`}
                      placeholder="Atlanta"
                    />
                    {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="state" className="text-gray-300">State *</Label>
                      <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
                        <SelectTrigger className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white ${errors.state ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          {statesList.map(state => (
                            <SelectItem key={state} value={state} className="text-white hover:bg-zinc-800">
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state && <p className="text-red-400 text-sm mt-1">{errors.state}</p>}
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="text-gray-300">ZIP *</Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                        className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.zipCode ? 'border-red-500' : ''}`}
                        placeholder="30067"
                        maxLength={5}
                      />
                      {errors.zipCode && <p className="text-red-400 text-sm mt-1">{errors.zipCode}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="housingType" className="text-gray-300">Housing Type *</Label>
                    <Select value={formData.housingType} onValueChange={(value) => handleInputChange('housingType', value)}>
                      <SelectTrigger className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white ${errors.housingType ? 'border-red-500' : ''}`}>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        {housingTypes.map(type => (
                          <SelectItem key={type} value={type} className="text-white hover:bg-zinc-800">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.housingType && <p className="text-red-400 text-sm mt-1">{errors.housingType}</p>}
                  </div>
                  <div>
                    <Label htmlFor="monthlyPayment" className="text-gray-300">Monthly Rent/Mortgage</Label>
                    <Input
                      id="monthlyPayment"
                      value={formData.monthlyPayment}
                      onChange={(e) => handleInputChange('monthlyPayment', e.target.value.replace(/\D/g, ''))}
                      className="mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500"
                      placeholder="$1,200"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Employment Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-teal-400" />
                Employment Information
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="employmentType" className="text-gray-300">Employment Status *</Label>
                  <Select value={formData.employmentType} onValueChange={(value) => handleInputChange('employmentType', value)}>
                    <SelectTrigger className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white ${errors.employmentType ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {employmentTypes.map(type => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-zinc-800">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.employmentType && <p className="text-red-400 text-sm mt-1">{errors.employmentType}</p>}
                </div>
                <div>
                  <Label htmlFor="monthlyIncome" className="text-gray-300">Monthly Income (Gross) *</Label>
                  <Input
                    id="monthlyIncome"
                    value={formData.monthlyIncome}
                    onChange={(e) => handleInputChange('monthlyIncome', e.target.value.replace(/\D/g, ''))}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.monthlyIncome ? 'border-red-500' : ''}`}
                    placeholder="$5,000"
                  />
                  {errors.monthlyIncome && <p className="text-red-400 text-sm mt-1">{errors.monthlyIncome}</p>}
                </div>

                {formData.employmentType && !['Retired', 'Unemployed', 'Student'].includes(formData.employmentType) && (
                  <>
                    <div>
                      <Label htmlFor="employerName" className="text-gray-300">Employer Name *</Label>
                      <Input
                        id="employerName"
                        value={formData.employerName}
                        onChange={(e) => handleInputChange('employerName', e.target.value)}
                        className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.employerName ? 'border-red-500' : ''}`}
                        placeholder="Company Name"
                      />
                      {errors.employerName && <p className="text-red-400 text-sm mt-1">{errors.employerName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="jobTitle" className="text-gray-300">Job Title *</Label>
                      <Input
                        id="jobTitle"
                        value={formData.jobTitle}
                        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                        className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.jobTitle ? 'border-red-500' : ''}`}
                        placeholder="Sales Manager"
                      />
                      {errors.jobTitle && <p className="text-red-400 text-sm mt-1">{errors.jobTitle}</p>}
                    </div>
                    <div>
                      <Label htmlFor="yearsEmployed" className="text-gray-300">Years at Current Job</Label>
                      <Input
                        id="yearsEmployed"
                        value={formData.yearsEmployed}
                        onChange={(e) => handleInputChange('yearsEmployed', e.target.value.replace(/\D/g, ''))}
                        className="mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500"
                        placeholder="3"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-700">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 text-sm">
                      All income sources will be considered. If you have additional income from other sources, 
                      please include the combined total in your monthly income.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Vehicle Information */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Car className="w-5 h-5 text-teal-400" />
                Vehicle Information
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="vehicleYear" className="text-gray-300">Year *</Label>
                  <Select value={formData.vehicleYear} onValueChange={(value) => handleInputChange('vehicleYear', value)}>
                    <SelectTrigger className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white ${errors.vehicleYear ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                      {vehicleYears.map(year => (
                        <SelectItem key={year} value={year} className="text-white hover:bg-zinc-800">
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.vehicleYear && <p className="text-red-400 text-sm mt-1">{errors.vehicleYear}</p>}
                </div>
                <div>
                  <Label htmlFor="vehicleMake" className="text-gray-300">Make *</Label>
                  <Select value={formData.vehicleMake} onValueChange={(value) => handleInputChange('vehicleMake', value)}>
                    <SelectTrigger className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white ${errors.vehicleMake ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                      {vehicleMakes.map(make => (
                        <SelectItem key={make} value={make} className="text-white hover:bg-zinc-800">
                          {make}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.vehicleMake && <p className="text-red-400 text-sm mt-1">{errors.vehicleMake}</p>}
                </div>
                <div>
                  <Label htmlFor="vehicleModel" className="text-gray-300">Model *</Label>
                  <Input
                    id="vehicleModel"
                    value={formData.vehicleModel}
                    onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.vehicleModel ? 'border-red-500' : ''}`}
                    placeholder="Camry"
                  />
                  {errors.vehicleModel && <p className="text-red-400 text-sm mt-1">{errors.vehicleModel}</p>}
                </div>
                <div>
                  <Label htmlFor="vinNumber" className="text-gray-300">VIN Number (Optional)</Label>
                  <Input
                    id="vinNumber"
                    value={formData.vinNumber}
                    onChange={(e) => handleInputChange('vinNumber', e.target.value.toUpperCase().slice(0, 17))}
                    className={`mt-1.5 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-teal-500 focus:ring-teal-500 ${errors.vinNumber ? 'border-red-500' : ''}`}
                    placeholder="1HGBH41JXMN109186"
                    maxLength={17}
                  />
                  {errors.vinNumber && <p className="text-red-400 text-sm mt-1">{errors.vinNumber}</p>}
                  <p className="text-gray-500 text-xs mt-1">17-character Vehicle Identification Number</p>
                </div>
              </div>

              <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-700">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 text-sm">
                      If you don't have a specific vehicle in mind yet, you can enter the approximate year, make, 
                      and model you're interested in. We'll help you find the perfect match!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-400" />
                Review Your Application
              </h3>

              <div className="space-y-6">
                {/* Personal Info Review */}
                <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-700">
                  <h4 className="text-teal-400 font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" /> Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Name:</span> <span className="text-white ml-2">{formData.firstName} {formData.lastName}</span></div>
                    <div><span className="text-gray-500">Email:</span> <span className="text-white ml-2">{formData.email}</span></div>
                    <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-2">{formData.phone}</span></div>
                    <div><span className="text-gray-500">DOB:</span> <span className="text-white ml-2">{formData.dateOfBirth}</span></div>
                    <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="text-white ml-2">{formData.streetAddress}, {formData.city}, {formData.state} {formData.zipCode}</span></div>
                  </div>
                </div>

                {/* Employment Review */}
                <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-700">
                  <h4 className="text-teal-400 font-medium mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Employment Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Status:</span> <span className="text-white ml-2">{formData.employmentType}</span></div>
                    <div><span className="text-gray-500">Monthly Income:</span> <span className="text-white ml-2">${Number(formData.monthlyIncome).toLocaleString()}</span></div>
                    {formData.employerName && <div><span className="text-gray-500">Employer:</span> <span className="text-white ml-2">{formData.employerName}</span></div>}
                    {formData.jobTitle && <div><span className="text-gray-500">Job Title:</span> <span className="text-white ml-2">{formData.jobTitle}</span></div>}
                  </div>
                </div>

                {/* Vehicle Review */}
                <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-700">
                  <h4 className="text-teal-400 font-medium mb-3 flex items-center gap-2">
                    <Car className="w-4 h-4" /> Vehicle Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Vehicle:</span> <span className="text-white ml-2">{formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}</span></div>
                    {formData.vinNumber && <div><span className="text-gray-500">VIN:</span> <span className="text-white ml-2">{formData.vinNumber}</span></div>}
                  </div>
                </div>
              </div>

              <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
                <p className="text-gray-300 text-sm">
                  By submitting this application, you authorize Legacy Motorplex to obtain consumer reports and verify 
                  the information provided. Your information will be kept secure and confidential.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-zinc-700">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="bg-transparent border-zinc-600 text-gray-300 hover:bg-zinc-800 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {currentStep < 4 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="bg-teal-500 hover:bg-teal-400 text-zinc-900 font-semibold"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-teal-500 hover:bg-teal-400 text-zinc-900 font-semibold min-w-[150px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreditApplicationForm;
